import axios from "axios";
import { base_url } from "../constants";

/**
 * Shared axios instances + a thin interceptor seam for the Stripe payment work.
 *
 * Two interceptors are attached to every instance:
 *  - request: re-reads the auth token from localStorage per request, so a
 *    long-lived tab (or one that started a payment before the JWT expired) never
 *    rides a stale token captured at module load. Historically the header was
 *    baked once at import; that is now a fallback default only.
 *  - response: on HTTP 401/403 it clears the session and returns to sign-in —
 *    keyed on the STATUS, not on a "Session Expired" message string, so an
 *    auth failure mid-payment can never be swallowed and mis-read as success.
 *    All errors are re-thrown so existing per-call catch/SimpleCatchError
 *    handling still runs (402/requires_action reaches callers unchanged).
 */

/** Safely read the parsed auth blob from localStorage; never throws. */
export const readAuthToken = () => {
  try {
    return JSON.parse(localStorage.getItem("data"));
  } catch {
    return null;
  }
};

/** Clear the session and return to sign-in (same behavior as Helper's 401 path). */
const clearSessionAndRedirect = () => {
  localStorage.clear();
  window.location.href = "/";
};

const initialToken = readAuthToken();

const attachInterceptors = (instance) => {
  instance.interceptors.request.use((config) => {
    const token = readAuthToken();
    if (token?.accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token.accessToken}`;
    }
    return config;
  });

  // 401 = the session is invalid → clear + return to sign-in (mirrors the legacy
  // "Session Expired" path, but keyed on status so it can't be swallowed mid-
  // payment). 403 is left to per-call handling: the backend uses it for
  // permission-denied sub-resources, which must NOT force a logout. Every error
  // is re-thrown so existing catch/SimpleCatchError still runs (402 surfaced).
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        clearSessionAndRedirect();
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

export const formAxios = attachInterceptors(
  axios.create({
    baseURL: base_url,
    headers: {
      ContentType: "multipart/form-data",
      Authorization: `Bearer ${initialToken?.accessToken}`,
    },
  })
);

export const Axios = attachInterceptors(
  axios.create({
    baseURL: base_url,
    headers: {
      Authorization: `Bearer ${initialToken?.accessToken}`,
      "ngrok-skip-browser-warning": "69420",
    },
  })
);
